import type { BlogArticleConfig } from "../types/blog";

type BlogTabProps = {
  blogPosts: BlogArticleConfig[];
  blogPage: number;
  blogTotalPages: number;
  setBlogPage: (page: number | ((current: number) => number)) => void;
  isLoading?: boolean;
  loadError?: string | null;
  onRetryLoad?: () => void;
  onSelectBlog: (slug: string) => void;
};

export default function BlogTab({
  blogPosts,
  blogPage,
  blogTotalPages,
  setBlogPage,
  isLoading = false,
  loadError = null,
  onRetryLoad,
  onSelectBlog
}: BlogTabProps) {
  if (isLoading) {
    return (
      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1140px] px-4 text-center text-sm text-[#6b7280] md:px-8 lg:px-10">
          Loading blog articles...
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1140px] rounded-3xl border border-[#fde68a] bg-[#fffbeb] px-6 py-10 text-center text-sm text-[#92400e] shadow-[0_18px_40px_rgba(15,23,36,0.08)] md:px-8 lg:px-10">
          <p>{loadError}</p>
          {onRetryLoad && (
            <button
              type="button"
              onClick={onRetryLoad}
              className="mt-5 rounded-xl bg-[#f7b500] px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-white"
            >
              Retry
            </button>
          )}
        </div>
      </section>
    );
  }

  if (blogPosts.length === 0) {
    return <section className="min-h-[420px] bg-white py-12 md:min-h-[520px] md:py-16" />;
  }

  return (
    <section className="bg-white py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
        <div className="grid gap-8 md:grid-cols-2">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="flex h-full flex-col overflow-hidden border border-[#eef2f6] bg-white shadow-[0_18px_40px_rgba(15,23,36,0.08)]"
            >
              <div className="relative">
                <img
                  src={post.basic.coverImageUrl}
                  alt={post.basic.title}
                  className="h-[260px] w-full object-cover md:h-[320px]"
                />
              </div>
              <div className="flex flex-1 flex-col px-8 pb-8 pt-8">
                <p className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#9aa3af]">
                  <span className="h-4 w-px bg-[#b8c0cc]" />
                  {post.basic.categoryLabel}
                </p>
                <h3 className="mt-4 text-[28px] font-black leading-tight text-[#111827]">
                  {post.basic.title}
                </h3>
                <p className="mt-5 text-[15px] leading-7 text-[#6b7280]">
                  {post.basic.excerpt}
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-y-3 text-sm text-[#8b95a5]">
                  <div className="flex items-center gap-2">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                    >
                      <circle
                        cx="12"
                        cy="8"
                        r="4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M4 20C4.8 16.8 8.1 14.5 12 14.5C15.9 14.5 19.2 16.8 20 20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span>{post.basic.authorEmail}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onSelectBlog(post.slug)}
                  className="mt-7 inline-flex w-fit rounded-md bg-[#f7b500] px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_28px_rgba(247,181,0,0.26)] transition hover:-translate-y-0.5"
                >
                  Read More
                </button>
              </div>
            </article>
          ))}
        </div>

        {blogTotalPages > 1 && (
          <div className="mt-12 flex items-center justify-between gap-4 border-t border-[#eef2f6] pt-6 text-sm text-[#1f2b3b]">
            <button
              type="button"
              onClick={() => setBlogPage((page) => Math.max(1, page - 1))}
              disabled={blogPage === 1}
              className="rounded-md border border-[#e5e7eb] px-3 py-1.5 text-sm text-[#1f2b3b] transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              {"上一页"}
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: blogTotalPages }, (_, index) => {
                const pageNumber = index + 1;
                const isActive = pageNumber === blogPage;
                return (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setBlogPage(pageNumber)}
                    aria-current={isActive ? "page" : undefined}
                    className={`flex h-8 w-8 items-center justify-center rounded text-[12px] font-semibold transition ${
                      isActive
                        ? "cursor-default bg-[#f3b455] text-[#1d1d1d]"
                        : "bg-[#0b1c2b] text-white hover:bg-[#122746]"
                    }`}
                    disabled={isActive}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setBlogPage((page) => Math.min(blogTotalPages, page + 1))}
              disabled={blogPage === blogTotalPages}
              className="rounded-md border border-[#e5e7eb] px-3 py-1.5 text-sm text-[#1f2b3b] transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              {"下一页"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
